import type { ReactNode } from "react";
import { Alert, Button, Checkbox, DatePicker, Descriptions, Input, Modal, Radio, Result, Select, Spin } from "@arco-design/web-react";

import { FINANCE_PAYMENT_REQUEST_ATTACHMENT_ACCEPT } from "../config";
import { useFinancePaymentRequestForm } from "../runtime/useFinancePaymentRequestForm";
import { useFinancePrepaymentSupplierPicker } from "../runtime/useFinancePrepayment";
import {
  FINANCE_INVOICE_STATUS_OPTIONS,
  FINANCE_PAYMENT_TYPE_OPTIONS,
  formatFinancePaymentRequestAmount,
  formatFinancePaymentRequestPayeeAccount,
} from "../service/financePaymentRequestService";
import type {
  FinanceInvoiceStatus,
  FinancePaymentRequestPayeeMode,
  FinancePaymentRequestResubmit,
  FinancePaymentRequestTarget,
  FinancePaymentType,
  FinancePrepaymentTarget,
} from "../types";
import "./financePaymentRequest.css";

type Props = {
  target: FinancePaymentRequestTarget;
  onClose: () => void;
  /** 修改后重新提交：回填原值、提交走重新提交；不给就是发起请款。 */
  resubmit?: FinancePaymentRequestResubmit;
};

/**
 * 请款表单：团和结算主体从应付清单那一行（重新提交时从原单）带出、不能改；可勾确认件里的行或直接填金额，
 * 收款账户选供应商已存的或手填，重新提交时还可以沿用原来的。
 */
export function FinancePaymentRequestFormModal({ target, onClose, resubmit }: Props) {
  const form = useFinancePaymentRequestForm(target, false, resubmit ?? null);
  return (
    <RequestModal title={resubmit ? "修改后重新提交" : "请款"} form={form} onClose={onClose} resubmit={Boolean(resubmit)}>
      <FormBody target={target} form={form} prepayment={false} />
    </RequestModal>
  );
}

/**
 * 给其他结算主体发起预付款：团从应付清单页带出；结算主体从能当结算主体的供应商里搜一家选上（这个团里已有确认协议的也能选）。
 * 款项类型固定为预付款、备注必填、不勾确认件行；这个团里它还没有确认协议时不受可请额度限制，其余同请款表单。
 */
export function FinancePrepaymentFormModal({ target, onClose }: { target: FinancePrepaymentTarget; onClose: () => void }) {
  const picker = useFinancePrepaymentSupplierPicker();
  const requestTarget: FinancePaymentRequestTarget | null = picker.picked
    ? { ...target, settlementSupplierId: picker.picked.supplierId, settlementSupplierName: picker.picked.supplierName }
    : null;
  const form = useFinancePaymentRequestForm(requestTarget, true);
  return (
    <RequestModal title="给其他结算主体发起预付款" form={form} onClose={onClose}>
      <div className="finance-payment-request-form">
        <Field label="结算主体">
          <Select
            aria-label="结算主体"
            showSearch
            filterOption={false}
            placeholder="输入名称搜索能当结算主体的供应商"
            value={picker.picked?.supplierId}
            options={picker.options.map((item) => ({ value: item.supplierId, label: item.supplierName }))}
            loading={picker.loading}
            disabled={form.submitting}
            onSearch={picker.search}
            onChange={(value: string) => picker.pick(value)}
          />
          {picker.failed ? <span className="finance-payment-request-error">结算主体搜索失败，请稍后重试。</span> : null}
        </Field>
        {requestTarget ? (
          <FormBody target={requestTarget} form={form} prepayment />
        ) : (
          <span className="finance-payment-request-muted">先选结算主体；这个团里还没有它的确认协议时，预付款不受可请额度限制。</span>
        )}
      </div>
    </RequestModal>
  );
}

type FormState = ReturnType<typeof useFinancePaymentRequestForm>;

/** 请款弹窗的外框：提交前是表单，提交成功后换成单号、流程和第一级审批人；提交中不能关掉。 */
function RequestModal({
  title,
  form,
  onClose,
  children,
  resubmit = false,
}: {
  title: string;
  form: FormState;
  onClose: () => void;
  children: ReactNode;
  resubmit?: boolean;
}) {
  const busy = form.uploading || form.submitting;
  const footer = form.result ? (
    <Button type="primary" onClick={onClose}>关闭</Button>
  ) : (
    <div className="finance-payment-request-footer">
      <Button onClick={onClose} disabled={form.submitting}>取消</Button>
      <Button
        type="primary"
        loading={form.submitting}
        disabled={!form.context.data || form.uploading}
        onClick={() => void form.submit()}
      >
        {resubmit ? "重新提交" : "提交请款"}
      </Button>
    </div>
  );
  return (
    <Modal
      title={title}
      visible
      style={{ width: 720 }}
      footer={footer}
      maskClosable={!busy}
      onCancel={() => { if (!form.submitting) onClose(); }}
      unmountOnExit
    >
      {form.result ? (
        <Result
          status="success"
          title={resubmit ? "已重新提交" : "请款已提交"}
          subTitle={`请款单号 ${form.result.requestNo}，走「${form.result.flowLabel}」，共 ${form.result.stepCount} 级；第一级审批人：${form.result.firstStepApprovers.map((item) => item.name).join("、")}。`}
        />
      ) : (
        children
      )}
    </Modal>
  );
}

/** 收款账户的几种填法：沿用原来的（只在重新提交时有）、选供应商已存的（有已存账户时）、手填。 */
function payeeModeOptions(form: FormState): Array<{ value: FinancePaymentRequestPayeeMode; label: string }> {
  return [
    ...(form.originalPayee ? [{ value: "original" as const, label: "沿用原收款账户" }] : []),
    ...(form.payeeAccounts.length > 0 ? [{ value: "saved" as const, label: "选供应商已存的" }] : []),
    { value: "manual", label: "手填" },
  ];
}

/**
 * 表单正文：上方是团、结算主体和三个数，下方是要填的字段。
 * 预付款：结算主体在上面选、这里不重复；款项类型固定、不勾确认件行、备注必填；还没有确认协议时写明不受可请额度限制。
 * 普通请款选了「预付款」类型时同样不出勾行区（已勾的在 runtime 里清掉）。
 */
function FormBody({
  target,
  form,
  prepayment,
}: {
  target: FinancePaymentRequestTarget;
  form: FormState;
  prepayment: boolean;
}) {
  const { context, draft } = form;
  if (context.isPending) return <Spin block className="finance-payment-request-loading" tip="正在读取可请额度…" />;
  if (context.isError || !context.data) {
    return (
      <Alert
        type="error"
        content="可请额度读取失败，请重试。"
        action={<Button size="mini" onClick={() => void context.refetch()}>重试</Button>}
      />
    );
  }
  const numbers = context.data;
  const unlimited = draft.paymentType === "prepayment" && !numbers.hasAgreement;
  return (
    <div className="finance-payment-request-form">
      <Descriptions
        column={2}
        className="finance-payment-request-summary"
        data={[
          { label: "团", value: target.itineraryTitle },
          ...(prepayment ? [] : [{ label: "结算主体", value: target.settlementSupplierName }]),
          { label: "当前应付", value: formatFinancePaymentRequestAmount(numbers.currentPayableAmount) },
          { label: "已请款（含审批中）", value: formatFinancePaymentRequestAmount(numbers.requestedAmount) },
          {
            label: "本次可请额度",
            value: unlimited ? (
              "不受可请额度限制（这个团里还没有它的确认协议）"
            ) : (
              <strong>{formatFinancePaymentRequestAmount(numbers.availableAmount)}</strong>
            ),
          },
        ]}
      />
      {draft.paymentType === "prepayment" ? null : <TickLines form={form} />}
      <Field label="款项类型">
        {prepayment ? (
          <span>预付款</span>
        ) : (
          <Radio.Group
            type="button"
            options={FINANCE_PAYMENT_TYPE_OPTIONS}
            value={draft.paymentType || undefined}
            onChange={(value: FinancePaymentType) => form.setField("paymentType", value)}
          />
        )}
      </Field>
      <Field label="本次请款金额">
        <Input
          aria-label="本次请款金额"
          prefix="¥"
          placeholder="大于 0，最多两位小数"
          value={draft.amount}
          onChange={(value) => form.setField("amount", value)}
        />
      </Field>
      <RestOfForm form={form} />
    </div>
  );
}

/** 依据确认件行：列出这个结算主体协议下的行，勾了显示合计；没有可勾的行时直接填金额。 */
function TickLines({ form }: { form: FormState }) {
  const { draft } = form;
  return (
    <Field label="依据确认件行（选填）">
      {form.lines.length > 0 ? (
        <>
          <Checkbox.Group
            direction="vertical"
            value={draft.lineIds}
            onChange={(value: string[]) => form.setLineIds(value)}
            options={form.lines.map((line) => ({
              value: line.lineId,
              label: (
                <span className="finance-payment-request-line">
                  <span>{`第 ${line.lineNo} 行 ${line.itemName}${line.itemSpec ? ` · ${line.itemSpec}` : ""}`}</span>
                  <span className="finance-payment-request-money">{formatFinancePaymentRequestAmount(line.amount)}</span>
                </span>
              ),
            }))}
          />
          <span className="finance-payment-request-muted">
            {form.lineTotal
              ? `勾选合计 ${formatFinancePaymentRequestAmount(form.lineTotal)}（${draft.lineIds.length} 行）`
              : "勾了行，请款金额默认等于勾选合计，可以改；也可以不勾、直接填金额。"}
          </span>
        </>
      ) : (
        <span className="finance-payment-request-muted">这个结算主体在确认件里没有可勾的行，直接填金额即可。</span>
      )}
    </Field>
  );
}

/**
 * 期望付款日期以下的字段（请款和预付款一样，只有备注的必填与否不同）以及提交前的提示。
 * 备注按当前款项类型标必填：预付款表单固定是预付款，普通请款选了「预付款」也一样，与提交时的校验一致。
 */
function RestOfForm({ form }: { form: FormState }) {
  const { draft } = form;
  const prepayment = draft.paymentType === "prepayment";
  const payeeModes = payeeModeOptions(form);
  return (
    <>
      <Field label="期望付款日期">
        <DatePicker
          value={draft.expectedPaymentDate || undefined}
          inputProps={{ "aria-label": "期望付款日期" }}
          onChange={(value) => form.setField("expectedPaymentDate", value ?? "")}
        />
      </Field>
      <Field label="票据状态">
        <Radio.Group
          type="button"
          options={FINANCE_INVOICE_STATUS_OPTIONS}
          value={draft.invoiceStatus || undefined}
          onChange={(value: FinanceInvoiceStatus) => form.setField("invoiceStatus", value)}
        />
      </Field>
      <Field label="收款账户">
        {payeeModes.length > 1 ? (
          <Radio.Group
            type="button"
            options={payeeModes}
            value={draft.payeeMode ?? undefined}
            onChange={(value: FinancePaymentRequestPayeeMode) => form.setField("payeeMode", value)}
          />
        ) : null}
        {draft.payeeMode === "manual" && form.payeeAccounts.length === 0 ? (
          <span className="finance-payment-request-muted">
            这个结算主体在供应商里还没存可用的收款账户，请手填；也可以先去「供应商」里给它补上收款账户。
          </span>
        ) : null}
        {draft.payeeMode === "original" ? <span>{form.originalPayee}</span> : null}
        {draft.payeeMode === "manual" && form.originalPayee ? (
          <span className="finance-payment-request-muted">原账号只显示后 4 位，手填要重新填完整的收款账号。</span>
        ) : null}
        {draft.payeeMode === "saved" ? (
          <Select
            aria-label="已存收款账户"
            placeholder="选一个收款账户"
            value={draft.payeeSourceAccountId ?? undefined}
            onChange={(value: string) => form.setField("payeeSourceAccountId", value)}
            options={form.payeeAccounts.map((account) => ({
              value: account.accountId,
              label: formatFinancePaymentRequestPayeeAccount(account),
            }))}
          />
        ) : null}
      </Field>
      {draft.payeeMode === "manual" ? (
        <>
          <Field label="收款户名">
            <Input aria-label="收款户名" maxLength={300} value={draft.payeeAccountName} onChange={(value) => form.setField("payeeAccountName", value)} />
          </Field>
          <Field label="开户行">
            <Input aria-label="开户行" maxLength={200} value={draft.payeeBankName} onChange={(value) => form.setField("payeeBankName", value)} />
          </Field>
          <Field label="开户支行（选填）">
            <Input aria-label="开户支行" maxLength={300} value={draft.payeeBankBranchName} onChange={(value) => form.setField("payeeBankBranchName", value)} />
          </Field>
          <Field label="收款账号">
            <Input aria-label="收款账号" maxLength={100} value={draft.payeeAccountNo} onChange={(value) => form.setField("payeeAccountNo", value)} />
          </Field>
        </>
      ) : null}
      <Field label="请款用途">
        <Input.TextArea aria-label="请款用途" maxLength={500} value={draft.purpose} onChange={(value) => form.setField("purpose", value)} />
      </Field>
      <Field label="所选部门">
        {form.departments.length > 0 ? (
          <Radio.Group
            options={form.departments.map((item) => ({
              value: item.deptId,
              label: item.isMain ? `${item.deptName}（主部门）` : item.deptName,
            }))}
            value={draft.departmentId ?? undefined}
            onChange={(value: number) => form.setField("departmentId", value)}
          />
        ) : (
          <Alert type="warning" content="你还没有归属部门，审批要按部门找审批人，请先在「组织和用户」里给你设置部门。" />
        )}
      </Field>
      <Field label="附件（PDF、PNG、JPEG，单个 20 MB，至少 1 个）">
        <label className="finance-payment-request-file-button">
          选择附件
          <input
            type="file"
            aria-label="选择附件"
            accept={FINANCE_PAYMENT_REQUEST_ATTACHMENT_ACCEPT}
            disabled={form.uploading}
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = "";
              if (file) void form.upload(file);
            }}
          />
        </label>
        {form.uploading ? <span className="finance-payment-request-muted">正在上传…</span> : null}
        {form.uploadError ? <span className="finance-payment-request-error">{form.uploadError}</span> : null}
        {draft.attachments.length > 0 ? (
          <ul className="finance-payment-request-files">
            {draft.attachments.map((item) => (
              <li key={item.attachmentId}>
                <span>{item.fileName}</span>
                <Button type="text" size="mini" onClick={() => form.removeAttachment(item.attachmentId)}>移除</Button>
              </li>
            ))}
          </ul>
        ) : null}
      </Field>
      <Field label={prepayment ? "备注（预付款必填，写明为什么要先付）" : "备注（选填）"}>
        <Input.TextArea aria-label="备注" maxLength={500} value={draft.remark} onChange={(value) => form.setField("remark", value)} />
      </Field>
      {form.errors.length > 0 ? (
        <Alert
          type="error"
          content={<ul className="finance-payment-request-error-list">{form.errors.map((item) => <li key={item}>{item}</li>)}</ul>}
        />
      ) : null}
      {form.serverError ? <Alert type="error" content={form.serverError} /> : null}
    </>
  );
}

/** 一个表单项：左边是名称，右边是控件；请款、预付款、登记已付、作废登记的弹窗共用。 */
export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="finance-payment-request-field">
      <span className="finance-payment-request-label">{label}</span>
      <div className="finance-payment-request-control">{children}</div>
    </div>
  );
}
